import * as React from 'react'
import clx from 'classnames'

import { StarIcon } from '@/app/features/icons/Star'
import { ChevronDownIcon } from '@/app/features/icons/ChevronDown'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { TableHeaderColumn } from '@/modules/drivers/types/table'

import { Header } from '@/app/types/creator'

import { getPrintsLabel } from './helper'

type Props = {
  title?: string
  headers: Header[]
  reports: any[]
  renderCell: (row: any, header: Header) => React.ReactNode
  isFavourite?: boolean
  defaultOpen?: boolean
}

export const ReportsList = ({
  title,
  headers,
  reports,
  renderCell,
  isFavourite = false,
  defaultOpen = true,
}: Props) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen)

  const columns = React.useMemo<TableHeaderColumn[]>(
    () =>
      headers.map((h) => ({
        title: h.label,
        key: h.name,
        type: 'string',
        sort: false,
      })),
    [headers],
  )

  const sectionTitle = title || getPrintsLabel(reports?.length ?? 0)
  const count = reports?.length ?? 0

  return (
    <section className="first:tw-mt-0 tw-flex tw-flex-col tw-border-b tw-border-primary/30">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        className="tw-flex tw-w-full tw-shrink-0 tw-items-center tw-gap-2 tw-bg-secondary tw-px-4 tw-py-2.5 tw-text-left tw-text-sm hover:tw-brightness-95"
      >
        <ChevronDownIcon
          className={clx(
            'tw-h-3 tw-w-3 tw-shrink-0 tw-text-content-base tw-transition-transform',
            !isOpen && '-tw-rotate-90',
          )}
        />
        {isFavourite && (
          <StarIcon className="tw-h-4 tw-w-4 tw-text-content-base" />
        )}
        <span className="tw-font-semibold">{count}</span>
        <span>{sectionTitle}</span>
      </button>

      {isOpen && (
        <div>
          <table className="tw-w-full tw-table-auto">
            <TableHeader
              opacity
              disabledCheckboxToggle
              columns={columns}
              sortConfig={null}
            />
            <tbody>
              {count > 0 ? (
                reports.map((row) => (
                  <tr
                    key={row?.id ?? JSON.stringify(row)}
                    className="tw-border-b tw-border-base-300 tw-bg-white"
                  >
                    {headers.map((h, idx) => {
                      const isFirst = idx === 0
                   
                      const tdStyle = isFirst
                        ? { width: '100%' }
                        : h.width
                          ? { width: h.width }
                          : undefined
                      return (
                        <td
                          key={h.name || `col-${idx}`}
                          className="tw-px-4 tw-py-2 tw-text-sm tw-text-content-base"
                          style={tdStyle}
                        >
                          {renderCell(row, h)}
                        </td>
                      )
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={headers.length}
                    className="tw-py-3.5 tw-text-center tw-text-sm tw-text-content-base/70"
                  >
                    No data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
